import { useEffect, useState } from 'react';
import { Button, Flex, Modal, TextInput } from '@mantine/core';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

interface Props {
	isOpen: boolean;
	isLoading: boolean;
	initialEmail?: string;
	onClose: () => void;
	onSubmit: (email: string) => void;
}

export const RepresentativeEmailModal = ({
	isOpen,
	isLoading,
	initialEmail,
	onClose,
	onSubmit,
}: Props) => {
	const tInvestor = useTranslationInvestor();
	const [email, setEmail] = useState(initialEmail ?? '');

	useEffect(() => {
		if (isOpen) {
			setEmail(initialEmail ?? '');
		}
	}, [isOpen, initialEmail]);

	const handleSubmit = () => {
		if (!email.trim()) {
			return;
		}
		onSubmit(email.trim());
	};

	const handleClose = () => {
		setEmail('');
		onClose();
	};

	return (
		<Modal
			opened={isOpen}
			onClose={handleClose}
			title={tInvestor('document_workflow.representative.modal_title')}
			centered
		>
			<Flex direction="column" gap="md" p="md">
				<TextInput
					label={tInvestor('document_workflow.representative.email_label')}
					placeholder={tInvestor('document_workflow.representative.email_placeholder')}
					type="email"
					value={email}
					onChange={(e) => setEmail(e.currentTarget.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							handleSubmit();
						}
					}}
				/>
				<Flex gap="sm" justify="flex-end">
					<Button variant="subtle" onClick={handleClose} disabled={isLoading}>
						{tInvestor('document_workflow.representative.cancel')}
					</Button>
					<Button
						variant="filled"
						disabled={!email.trim() || isLoading}
						loading={isLoading}
						onClick={handleSubmit}
					>
						{tInvestor('document_workflow.representative.send')}
					</Button>
				</Flex>
			</Flex>
		</Modal>
	);
};
